import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { StoreSettings } from "../types";
import { pickRealStore } from "@/lib/merchantStore";

export const useActiveStore = () => {
  const { toast } = useToast();
  const [store, setStore] = useState<StoreSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const fetchStore = useCallback(async (isRetry = false) => {
    try {
      setHasError(false);
      if (!isRetry) setIsLoading(true);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) {
        setStore(null);
        setIsLoading(false);
        return;
      }

      // Real store first (store_settings), never the empty auto-created "my-store"
      // stores row; the stores row is only a fallback for accounts without settings.
      const [{ data: settingsRows, error: settingsError }, { data: storesRows, error: storeError }] = await Promise.all([
        supabase.from('store_settings').select('*').eq('user_id', user.id),
        supabase.from('stores').select('*').eq('owner_id', user.id),
      ]);

      if (settingsError) throw settingsError;
      if (storeError) throw storeError;

      const picked = pickRealStore(settingsRows as any[], storesRows as any[]);
      if (picked) {
        setStore(picked.row as unknown as StoreSettings);
        setIsLoading(false);
        return;
      }

      // No store found — retry once if first attempt
      if (!isRetry) {
        await new Promise(r => setTimeout(r, 1000));
        await fetchStore(true);
        return;
      }

      setStore(null);
    } catch (err) {
      console.error('Erreur récupération boutique active:', err);
      setHasError(true);
      if (!isRetry) {
        // Auto-retry once on error
        await new Promise(r => setTimeout(r, 1500));
        await fetchStore(true);
        return;
      }
      toast({
        title: "Erreur",
        description: "Impossible de charger votre boutique active",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStore();
  }, [fetchStore]);

  return { store, isLoading, hasError, refetch: () => fetchStore(false) };
};
